import cx from "classnames";
import { useParams, Link } from "react-router-dom";
import Helmet from "react-helmet";

import { useTranslation } from "@mywheels/common/lib/translation";
import { useGraphCMS } from "@mywheels/common/lib/useGraphCMS";

import {
  SocialInstagramIcon,
  SocialFacebookIcon,
  SocialTwitterIcon,
} from "web/ui/icons/social";
import { LinkArrow, Markdown, ScrollToTop } from "web/ui";
import { useLangPrefixify, useRoutes } from "web/lib/i18n";

import { _blogPicture } from "./styles";
import { PostMeta } from "./PostMeta";
import { ToolbarSpacer } from "web/layout/Toolbar";
import { NotFoundPage } from "../NotFoundPage";
import { BlogHero } from "./BlogHero";
import {
  BlogPostSearchResult,
  blogPostUrl,
  GetBlogPostBySlugQuery,
  SearchBlogPostsByCategorySlugQuery,
} from "./data";

export default function BlogPostPage() {
  const { t } = useTranslation("page:BlogPost");
  const routes = useRoutes();

  const {
    year,
    month,
    slug = "",
  } = useParams<{
    year?: string;
    month?: string;
    slug?: string;
  }>();

  const { data: post } = useGraphCMS(GetBlogPostBySlugQuery, {
    slug,
  });

  const { data: relatedPosts } = useGraphCMS(
    SearchBlogPostsByCategorySlugQuery,
    post?.categories[0] && {
      categorySlug: post.categories[0].slug,
      limit: 4,
      offset: 0,
    }
  );

  if (post && !(post.publishDate || "").startsWith(`${year}-${month}`)) {
    // TODO make this work layout-stylistically, somehow
    return (
      <>
        <ToolbarSpacer className="bg-gray" />
        <NotFoundPage />
      </>
    );
  }

  return (
    <>
      {post && (
        <Helmet>
          <title>{post.title}</title>
          <meta name="description" content={post.excerpt} />
          <link
            href={routes.BlogPost.url({
              year: year as any, // eslint-disable-line
              month: month as any, // eslint-disable-line
              slug,
            })}
            rel="canonical"
          />
        </Helmet>
      )}

      <ScrollToTop reactToSearchChange>
        <BlogHero small className="pb-8">
          <div className="absolute inset-0 bg-gray900 bg-opacity-20" />
          <div className="relative container text-white">
            <div className="row">
              <div className="col-12 md:col-7">
                <h1 className="text-white md:text-5xl mb-5">
                  <Link
                    to={routes.Blog.url()}
                    style={{ textDecoration: "none", color: "white" }}
                  >
                    {t("MyWheels Blog")}
                  </Link>
                </h1>
              </div>
            </div>
          </div>
        </BlogHero>

        <div className="container mb-7">
          <div className="row">
            <div className="col-12 md:col-8">
              {post && (
                <div>
                  <div className="mt-4 whitespace-nowrap">
                    <Link className="breadcrumb" to={routes.Blog.url()}>
                      {t("Blog")}
                    </Link>
                    {/* {mainKeyword && (
                      <Link
                        className="breadcrumb"
                        to={routes.BlogArchive.url({
                          filter: slugify(mainKeyword),
                        })}
                      >
                        {capitalizeKeyword(mainKeyword)}
                      </Link>
                    )} */}
                  </div>
                  <h1>{post.title}</h1>
                  <PostMeta post={post} />
                  {post.featuredImage && (
                    <div
                      className={cx(_blogPicture, "mt-8 mb-4")}
                      style={{
                        backgroundImage: `url("https://media.graphcms.com/output=format:jpg/resize=,height:800,fit:max/${post.featuredImage.handle}")`,
                        height: "300px",
                      }}
                    />
                  )}
                  {post.content && (
                    <Markdown source={post.content} className="prose" />
                  )}
                </div>
              )}
            </div>
            <div className="md:col-1" />
            <div className="col-12 md:col-3">
              <BecomeManagerCard />
              {relatedPosts && relatedPosts.pageInfo.pageSize > 0 && (
                <div className="mt-10">
                  <h4 className="text-blue mb-5">
                    {t("Gerelateerde artikelen")}
                  </h4>
                  {relatedPosts.edges.map((edge) => (
                    <RelatedPostCard key={edge.node.id} post={edge.node} />
                  ))}
                </div>
              )}
              <h4 className="mt-10 text-blue mb-2">Follow us</h4>
              <a
                href="https://www.instagram.com/mywheelsnl/"
                className="inline-flex border-0 bg-none text-gray400 hover:text-blue mr-4"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialInstagramIcon size={32} />
              </a>
              <a
                href="https://www.facebook.com/mywheelsnl"
                className="inline-flex border-0 bg-none text-gray400 hover:text-blue mr-4"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialFacebookIcon size={32} />
              </a>
              <a
                href="https://twitter.com/my_wheels"
                className="inline-flex border-0 bg-none text-gray400 hover:text-blue mr-4"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialTwitterIcon size={32} />
              </a>
            </div>
          </div>
        </div>
      </ScrollToTop>
    </>
  );
}

function BecomeManagerCard() {
  const { t } = useTranslation("page:BlogPost");
  const routes = useRoutes();

  return (
    <div className="-mx-4 mb-5 p-4 rounded border shadow-md">
      <h5 className="mb-4 font-display text-lg">
        {t("Wil jij ook meehelpen?")}
      </h5>
      <p>
        {t(
          "MyWheels zou niet mogelijk zijn zonder ons toegewijd netwerk aan sleutelfiguren. Alle MyWheels deelauto's hebben een sleutelfiguur."
        )}
      </p>
      <Link
        className="font-display underline text-gray hover:text-gray700"
        to={routes.Beheerders.url()}
      >
        {t("Lees meer")}
        <LinkArrow />
      </Link>
    </div>
  );
}

function RelatedPostCard({ post }: { post: BlogPostSearchResult }) {
  const { t } = useTranslation("page:BlogPost");
  const lp = useLangPrefixify();

  return (
    <div className="mb-4 mr-4">
      <div>
        <div className="font-display">{post.title}</div>
        <Link
          className="font-display text-sm underline text-gray hover:text-gray700"
          to={lp(blogPostUrl(post))}
        >
          {t("Lees meer")}
          <LinkArrow />
        </Link>
      </div>
    </div>
  );
}
